import React, { useMemo } from "react";
import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import aapmorlogo from "../../assets/AAPMOR LOGO.svg";
import aapmorlighttext from "../../assets/aapmorwhitetext.svg";
import aapmortext from "../../assets/aapmortext.svg";

const footerLinkSx = {
  color: "var(--home-muted)",
  fontSize: "0.92rem",
  fontWeight: 500,
  textDecoration: "none",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "var(--home-accent)",
    textDecoration: "none",
  },
};

const externalLinkSx = {
  ...footerLinkSx,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
};

const Footer = () => {
  const mode = useMemo(() => {
    if (typeof window === "undefined") return false;
    const storedTheme = window.localStorage.getItem("theme");
    return storedTheme ? JSON.parse(storedTheme) : false;
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <Paper
      elevation={0}
      component="footer"
      sx={{
        mt: "auto",
        borderTop: "1px solid var(--home-border)",
        borderRadius: 0,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(248,243,233,0.96) 100%)",
        backdropFilter: "blur(18px) saturate(130%)",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 3.5, md: 4 },
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 3, lg: 4 }}
          alignItems={{ xs: "flex-start", lg: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={1.25} sx={{ maxWidth: 420 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  border: "1px solid rgba(var(--accent-primary-rgb),0.2)",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(245,238,226,0.92) 100%)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 10px 24px rgba(15, 14, 12, 0.08)",
                }}
              >
                <img
                  src={aapmorlogo}
                  alt="AAPMOR logo"
                  style={{ width: 24, height: 24, display: "block" }}
                  width="24"
                  height="24"
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                {!mode ? (
                  <img
                    src={aapmortext}
                    alt="AAPMOR"
                    style={{
                      width: 128,
                      height: 20,
                      maxWidth: "100%",
                      display: "block",
                    }}
                    width="128"
                    height="20"
                  />
                ) : (
                  <img
                    src={aapmorlighttext}
                    alt="AAPMOR"
                    style={{
                      width: 128,
                      height: 20,
                      maxWidth: "100%",
                      display: "block",
                    }}
                    width="128"
                    height="20"
                  />
                )}
                <Typography
                  sx={{
                    color: "var(--home-muted)",
                    fontSize: "0.85rem",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mt: 0.5,
                  }}
                >
                  Blogs and publishing platform
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                color: "var(--home-muted)",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                maxWidth: 420,
              }}
            >
              For company updates, and internal publishing. For official company
              information, visit the main site.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2.5, sm: 5 }}
            sx={{ flexWrap: "wrap" }}
          >
            <Stack spacing={1.25}>
              <Typography
                sx={{
                  color: "var(--home-ink)",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Main Site
              </Typography>
              <Link
                href="https://aapmor.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={externalLinkSx}
              >
                aapmor.com
              </Link>
              <Link
                href="https://blogs.aapmor.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={externalLinkSx}
              >
                Blogs portal
              </Link>
            </Stack>

            <Stack spacing={1.25}>
              <Typography
                sx={{
                  color: "var(--home-ink)",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Policies
              </Typography>
              <Link
                component={RouterLink}
                to="/terms-and-conditions"
                sx={footerLinkSx}
              >
                Terms and Conditions
              </Link>
              <Link
                component={RouterLink}
                to="/privacy-policy"
                sx={footerLinkSx}
              >
                Privacy Policy
              </Link>
            </Stack>

            <Stack spacing={1.25}>
              <Typography
                sx={{
                  color: "var(--home-ink)",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Notes
              </Typography>
              <Typography
                sx={{ color: "var(--home-muted)", fontSize: "0.92rem" }}
              >
                Internal publishing and editorial updates
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Divider
          sx={{ my: { xs: 2.5, md: 3 }, borderColor: "var(--home-border)" }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography sx={{ color: "var(--home-muted)", fontSize: "0.88rem" }}>
            Copyright {currentYear} Aapmor Inc. All rights reserved.
          </Typography>

          <Typography sx={{ color: "var(--home-muted)", fontSize: "0.88rem" }}>
            Built for AAPMOR blogs, announcements, and knowledge sharing.
          </Typography>
        </Stack>
      </Container>
    </Paper>
  );
};

export default Footer;
